import { CapaDeModal } from '@/shared/components/CapaDeModal'
import { estiloDeBoton } from '@/shared/lib/estilosDeBoton'
import SelectBuscable from '@/shared/components/SelectBuscable'
import { Checkbox } from '@/shared/components/Checkbox'
import { useState, useEffect } from "react";
import { AlertCircle, X } from "lucide-react";
import { mensajeDeError } from "@/shared/lib/mensajeDeError";

export interface FieldOption {
  value: string | number;
  label: string;
}

export interface FormField<T> {
  name: keyof T | string;
  label: string;
  type?: "text" | "number" | "select" | "textarea" | "checkbox";
  required?: boolean;
  options?: FieldOption[];
  placeholder?: string;
  disabled?: boolean;
  /**
   * El mismo numero que valida el backend. Ademas de frenar el tecleo, se
   * muestra bajo el campo: sin eso el limite solo se descubre chocando con el.
   */
  maxLength?: number;
  /**
   * Valor con el que nace el campo al crear. Hace falta para las casillas: sin
   * el, una casilla nueva se pinta desmarcada aunque el backend guarde `true`
   * por omision, y lo que se ve contradice lo que se guarda.
   */
  defaultValue?: string | number | boolean;
}

interface CatalogFormModalProps<T> {
  title: string;
  fields: Array<FormField<T>>;
  initial?: Partial<T>;
  onClose: () => void;
  onSubmit: (data: Partial<T>) => Promise<void> | void;
}

function CatalogFormModal<T>({
  title,
  fields,
  initial = {},
  onClose,
  onSubmit,
}: CatalogFormModalProps<T>) {
  // Only keep fields declared in the form spec
  const initialPicked = Object.fromEntries(
    fields.map((f) => {
      const k = String(f.name);
      const valor = (initial as any)[k];
      return [k, valor === undefined ? f.defaultValue : valor];
    })
  );
  const [form, setForm] = useState<Record<string, any>>(() => initialPicked);
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [touched, setTouched] = useState<Record<string, boolean>>({});
  const [errorDelServidor, setErrorDelServidor] = useState<string | null>(null);

  /**
   * Todo cambio de campo borra el error del servidor: dejarlo puesto mientras
   * el usuario corrige hace pensar que la correccion tampoco sirvio.
   */
  const actualizaCampo = (
    fieldName: string,
    valor: string | number | boolean,
  ) => {
    setErrorDelServidor(null);
    setForm((prev) => ({ ...prev, [fieldName]: valor }));
  };

  // Validar campos requeridos
  useEffect(() => {
    const newErrors: Record<string, string> = {};
    fields.forEach((field) => {
      const fieldName = String(field.name);
      if (field.required && touched[fieldName]) {
        const value = form[fieldName];
        if (!value || (typeof value === "string" && value.trim() === "")) {
          newErrors[fieldName] = `${field.label} es requerido`;
        }
      }
    });
    setErrors(newErrors);
  }, [form, fields, touched]);

  const handleBlur = (fieldName: string) => {
    setTouched((prev) => ({ ...prev, [fieldName]: true }));
  };

  const isFormValid = () => {
    // Verificar que todos los campos requeridos estén completos
    return fields.every((field) => {
      if (!field.required) return true;
      const value = form[String(field.name)];
      return value && (typeof value !== "string" || value.trim() !== "");
    });
  };

  const handleSubmit = async () => {
    // Marcar todos los campos como tocados para mostrar errores
    const allTouched = fields.reduce<Record<string, boolean>>((acc, f) => {
      acc[String(f.name)] = true;
      return acc;
    }, {});
    setTouched(allTouched);

    if (!isFormValid()) {
      return;
    }

    setLoading(true);
    setErrorDelServidor(null);
    try {
      // Build payload with only whitelisted fields
      const payload = fields.reduce<Record<string, any>>((acc, f) => {
        const k = String(f.name);
        if (form[k] !== undefined && form[k] !== "") acc[k] = form[k];
        return acc;
      }, {});
      await onSubmit(payload as Partial<T>);
      onClose();
    } catch (errCrudo: unknown) {
      // Sin este catch la promesa quedaba rechazada sin que nadie la leyera:
      // el modal se quedaba abierto, sin aviso, y el usuario solo veia que
      // Guardar no hacia nada. Asi fue como paso inadvertido el limite de la
      // descripcion del puesto.
      setErrorDelServidor(
        mensajeDeError(errCrudo, "No se pudo guardar. Intenta de nuevo.")
      );
    } finally {
      setLoading(false);
    }
  };

  const renderField = (field: FormField<T>) => {
    const fieldName = String(field.name);
    const hasError = touched[fieldName] && errors[fieldName];

    /**
     * Las banderas del catalogo no se pueden expresar con un select: sus
     * opciones viajan como texto y el backend valida con @IsBoolean, asi que
     * "true" llegaria como cadena y seria rechazado.
     */
    if (field.type === "checkbox") {
      return (
        <Checkbox
          checked={Boolean(form[fieldName])}
          onChange={(e) => actualizaCampo(fieldName, e.target.checked)}
          disabled={field.disabled}
          etiqueta={field.placeholder}
        />
      );
    }

    if (field.type === "select") {
      return (
        <SelectBuscable
          variante="formulario"
          value={String(form[fieldName] ?? "")}
          onChange={(texto) => {
            // Number() nunca devuelve null, devuelve NaN, asi que el ?? que
            // habia aqui nunca se aplicaba y una opcion no numerica se
            // guardaba como NaN. Se conserva el texto en ese caso.
            const num = Number(texto)
            const valor = texto === '' || Number.isNaN(num) ? texto : num
            actualizaCampo(fieldName, valor)
          }}
          disabled={field.disabled}
          error={Boolean(hasError)}
          placeholder={field.placeholder || `Seleccionar ${field.label.toLowerCase()}`}
          opciones={(field.options ?? []).map((opt) => ({ value: String(opt.value), label: opt.label }))}
        />
      );
    }

    if (field.type === "textarea") {
      return (
        <textarea
          value={form[fieldName] ?? ""}
          onChange={(e) => actualizaCampo(fieldName, e.target.value)}
          onBlur={() => handleBlur(fieldName)}
          disabled={field.disabled}
          maxLength={field.maxLength}
          rows={3}
          className={`w-full px-3 py-2 border-0 rounded-md bg-[#F6F9FB] focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors ${
            hasError ? "border-red-500 bg-red-50" : "border-gray-300"
          } ${field.disabled ? "bg-gray-100 cursor-not-allowed" : ""}`}
          placeholder={field.placeholder}
        />
      );
    }

    return (
      <input
        type={field.type === "number" ? "number" : "text"}
        value={form[fieldName] ?? ""}
        onChange={(e) =>
          actualizaCampo(
            fieldName,
            field.type === "number" ? Number(e.target.value) : e.target.value
          )
        }
        onBlur={() => handleBlur(fieldName)}
        disabled={field.disabled}
        maxLength={field.type === "number" ? undefined : field.maxLength}
        className={`w-full px-3 py-2 border-0 rounded-md bg-[#F6F9FB] focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors ${
          hasError ? "border-red-500 bg-red-50" : "border-gray-300"
        } ${field.disabled ? "bg-gray-100 cursor-not-allowed" : ""}`}
        placeholder={field.placeholder}
      />
    );
  };

  return (
    <CapaDeModal>
      <div className="bg-[#ffffff] rounded-lg shadow-xl max-w-3xl w-full mx-4">
        <div className="flex items-center justify-between p-6">
          <h3 className="text-xl font-semibold text-[#1E1E1F]">{title}</h3>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 h-[24px] w-[24px] bg-[#D9D9D9] rounded-full p-1 hover:bg-[#D9D9D9]/60"
            disabled={loading}
          >
            <X className="h-4 w-4" color="#7e7e94ff" />
          </button>
        </div>
        <div className="p-4 space-y-4 max-h-[60vh] overflow-y-auto">
          {errorDelServidor && (
            <div className="flex items-start gap-2 rounded-md bg-red-50 p-3 text-sm text-red-700">
              <AlertCircle className="h-4 w-4 mt-0.5 shrink-0" />
              <span>{errorDelServidor}</span>
            </div>
          )}
          {fields.map((field) => {
            const fieldName = String(field.name);
            const hasError = touched[fieldName] && errors[fieldName];

            return (
              <div key={fieldName}>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {field.label}
                  {field.required && (
                    <span className="text-red-500 ml-1">*</span>
                  )}
                </label>
                {renderField(field)}
                {field.maxLength && field.type !== "select" && (
                  <div className="flex items-center justify-between gap-2 mt-1 text-xs text-gray-500">
                    <span>Este campo tiene un límite de {field.maxLength} caracteres</span>
                    <span
                      className={
                        String(form[fieldName] ?? "").length >= field.maxLength
                          ? "text-amber-600 font-medium"
                          : ""
                      }
                    >
                      {String(form[fieldName] ?? "").length}/{field.maxLength}
                    </span>
                  </div>
                )}
                {hasError && (
                  <div className="flex items-center gap-1 mt-1 text-red-600 text-xs">
                    <AlertCircle className="h-3 w-3" />
                    <span>{errors[fieldName]}</span>
                  </div>
                )}
              </div>
            );
          })}
        </div>
        <div className="p-4 flex justify-end gap-2">
          <button
            onClick={onClose}
            className={estiloDeBoton('secundario')}
          >
            Cancelar
          </button>
          <button
            onClick={handleSubmit}
            disabled={loading || !isFormValid()}
            className={estiloDeBoton('primario')}
          >
            {loading ? "Guardando..." : "Guardar"}
          </button>
        </div>
      </div>
    </CapaDeModal>
  );
}

export default CatalogFormModal;
